import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { SearchInputWrapper } from "../../../components/common/datatable/filter/SearchInputWrapper";
import React, { useEffect, useState } from "react";
import Modal from "../../../components/common/form/Modal";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { useGetPrimaryCollectorByStateAndCountyQuery, useSearchAppraisalRollsQuery } from "../../data/appraisalRoll/appraisalRollsApi";
import ButtonTray from "../../../components/common/form/ButtonTray";
import { useGetEnumsQuery } from "../../../app/globalApi";
import { useGetAllStatesQuery } from "../../data/states/statesApi";
import { populateStateAndCounty } from "../../../helpers/utils/OrderDetailUtils";
import CollectorSearchInputWrapper from "../../../components/common/datatable/filter/CollectorSearchInputWrapper";
import { skipToken } from "@reduxjs/toolkit/query";

type Props = {
    show: boolean;
    setShow: (val: boolean) => void;
    createOrderData: any,
    setCreateOrderData: React.Dispatch<React.SetStateAction<any>>,
    countyName?: any,
    stateAbbr: any
}

export const OrderPropertyPickerModal: React.FC<Props> = ({ show, setShow, createOrderData, setCreateOrderData, countyName,
    stateAbbr, }) => {

    const [selectedPropertyList, setSelectedPropertyList] = useState<any[]>([]);
    const [render, setRender] = useState(null);
    const [disable, setDisable] = useState(false);
    const { data: enums } = useGetEnumsQuery("");
    const statesApiResult = useGetAllStatesQuery("");
    const [collectorValue, setCollectorValue] = useState(null);
    const primaryCollectorData = useGetPrimaryCollectorByStateAndCountyQuery(countyName ? {
        stateAbbr: stateAbbr, countyName: countyName
    } : skipToken);

    useEffect(() => {
        if (primaryCollectorData?.data) {
            setCollectorValue(primaryCollectorData?.data?.name);
        }


    }, [primaryCollectorData]);
    const columns: DataTableColumn[] = [

        {
            label: "",
            accessor: "id", canSort: false,
            cell: (props: any) => {
                return <>
                    <input
                        type="checkbox"
                        onChange={() => {
                            setSelectedPropertyList((prevList) => {
                                const exists = prevList.some(
                                    (item) => item.id === props.row.original.id
                                );
                                return exists
                                    ? prevList.filter((item) => item.id !== props.row.original.id)
                                    : [...prevList, props.row.original];
                            });
                        }}
                        onClick={() => setRender(props.row.original.id + Date.now())}
                        checked={selectedPropertyList.some(
                            (item) => item.id === props.row.original.id
                        )}
                    />
                </>
            }
        },

        {
            label: "Tax Year", accessor: "taxYear",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"number"} placeholder={"Tax Year..."} name={"taxYear"}
                    accessor={"taxYear"} />,
                overrideFilterLabel: "Tax Year",
                overrideFilterAccessor: "taxYear"
            }]
        },


        {
            label: "Tax ID", accessor: "taxAccountNumber",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Tax Account No..."}
                    name={"taxAccountNumber"} accessor={"taxAccountNumber"}
                />,
                overrideFilterLabel: "Tax ID",
                overrideFilterAccessor: "taxAccountNumber"
            }]
        },

        {
            label: "Geo ID", accessor: "geographicId",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Geo Id..."}
                    name={"geographicId"} accessor={"geographicId"} />,
                overrideFilterLabel: "Geo Id",
                overrideFilterAccessor: "geographicId"
            }]
        },

        {
            label: "Collector",
            accessor: "collectorName",
            overrideSortColumn: "collectorId",
            filterOptions: [{
                filterComponent: <CollectorSearchInputWrapper value={collectorValue || ''} accessor="collectorId" />,
                overrideFilterAccessor: "collectorId",
                overrideFilterLabel: "Collector",
            }]
        },
        {
            label: "Address", accessor: "situsAddress",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Address..."}
                    name={"situsAddress"} accessor={"situsAddress"} />,
                overrideFilterLabel: "Situs Address",
                overrideFilterAccessor: "situsAddress"
            }]
        },

        {
            label: "Subdivision", accessor: "subdivisionName",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Subdivision Name..."}
                    name={"subdivision"} accessor={"subdivisionName"} />,
                overrideFilterLabel: "Subdivision Name",
                overrideFilterAccessor: "subdivisionName"
            }]
        },

        {
            label: "Sub Code", accessor: "subdivisionCode",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Sub Code..."}
                    name={"subdivisionCode"} accessor={"subdivisionCode"} />,

                overrideFilterAccessor: "subdivisionCode"
            }]
        },

        {
            label: "Owner", accessor: "ownerName",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Owner Name..."}
                    name={"ownerName"} accessor={"ownerName"}
                />,
                overrideFilterLabel: "Owner Name",
                overrideFilterAccessor: "ownerName"
            }]
        },

        {
            label: "Legal", accessor: "legalDescription",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Legal Description..."}
                    name={"legalDescription"} accessor={"legalDescription"}
                />,
                overrideFilterLabel: "Legal Description",
                overrideFilterAccessor: "legalDescription"
            }]
        },

    ]

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (selectedPropertyList.length === 0) return;

        setDisable(true);
        let currentPropCount = createOrderData?.properties?.length || 0;

        const newProperties = await Promise.all(
            selectedPropertyList.map(async (property, index) => {
                const { state, countyName } = await populateStateAndCounty(property.collectorId);

                const stateLabel = statesApiResult?.data?.find(
                    (el: any) => el.stateAbbreviation === state
                )?.stateName;

                const propertyTypeEnum = enums?.["PropertyType"]?.find(
                    (el: any) => el.constant === "RESIDENTIAL"
                );

                const newSequence = currentPropCount + index + 1;

                return {
                    sequence: newSequence,
                    address: {
                        streetAddress: property.situsAddress,
                        state: {
                            label: stateLabel || state,
                            value: state
                        },
                        zip: property.zip
                    },
                    accountNumber: property.taxAccountNumber,
                    countyName: {
                        label: countyName,
                        value: countyName
                    },
                    legalDescription: property.legalDescription,
                    propertyRequestSource: "PICKER",
                    subdivisionName: property.subdivisionName,
                    propertyType: {
                        value: propertyTypeEnum?.constant || "RESIDENTIAL",
                        label: propertyTypeEnum?.value || "Residential"
                    },
                    owner: property.ownerName
                };
            })
        );

        setCreateOrderData((prev: any) => ({
            ...prev,
            properties: [...(prev?.properties || []), ...newProperties]
        }));

        setShow(false);
    }

    return (

        <>
            <Modal
                title={"Property Picker"}
                show={show}
                setShow={setShow}
                className="w-3/4 bg-text-0 rounded-lg shadow-lg"
                fields={
                    <div
                        className="bg-text-0 modal-body max-h-[calc(100vh-200px)] overflow-auto [-ms-overflow-style:none] [scrollbar-width:none]">

                        <DataTable
                            columns={columns}
                            actions={{
                                fetchAction: useSearchAppraisalRollsQuery,
                            }}
                            defaultSortOptions={{
                                accessor: "taxYear",
                                direction: "desc",
                            }}
                            defaultFilter={{
                                pageNumber: 0,
                                pageSize: 5,
                                sort: "taxYear",
                                sortOrder: "DESCENDING"

                            }}
                            defaultFilterFormExpanded={true}
                            renderOptions={{
                                skipUserSettingRender: true,
                                skipFilterButtonRender: true,

                            }}
                            appendFilterToUrl={false}
                            basePermission={"AUTOHOA"}
                        />

                        {selectedPropertyList.length > 0 && (
                            <div className="mt-3">
                                <h1 className="mb-2 text-2xl font-semibold">Selected Property</h1>
                                <table className="table table-hover table-striped">
                                    <thead className="bg-highlight-5 text-text-0 relative text-left">
                                        <tr>
                                            <th>Tax Year</th>
                                            <th>Tax ID</th>
                                            <th>Geo ID</th>
                                            <th>Collector</th>
                                            <th>Address</th>
                                            <th>Subdivision</th>
                                            <th>Owner</th>
                                            <th>Legal</th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        className={`text-left ${selectedPropertyList.length > 4 ? "table_height" : ""
                                            }`}
                                    >
                                        {selectedPropertyList.map((current: any) => (
                                            <tr key={`${current.id}-${current.taxYear}`}>
                                                <td>{current.taxYear}</td>
                                                <td>{current.taxAccountNumber}</td>
                                                <td>{current.geographicId}</td>
                                                <td>{current.collectorName ?? "-"}</td>
                                                <td>{current.situsAddress}</td>
                                                <td>{current.subdivisionName}</td>
                                                <td>{current.ownerName}</td>
                                                <td>{current.legalDescription}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}


                        <ButtonTray
                            buttons={[
                                {
                                    buttonProps: {
                                        btnText: "Cancel",
                                        onClick: () => setShow(false),
                                        type: "cancel",
                                        isDisabled: disable,
                                    },
                                    buttonType: "button",
                                },
                                {
                                    buttonProps: {
                                        btnText: "Add",
                                        // @ts-ignore
                                        onClick: handleSubmit,
                                        type: "submit",
                                        isDisabled: disable || selectedPropertyList.length == 0,
                                        isLoading: disable,
                                    },
                                    buttonType: "button",
                                },
                            ]}
                            align="start"
                            gap={2}
                        />
                    </div>
                }
            />
        </>
    )
}